import React, { useEffect, useState, useCallback } from 'react';
import { db, collection, query, orderBy, limit, getDocs, startAfter, updateDoc, getDoc, doc } from '..';
import { Button, Container } from 'react-bootstrap';
import { List } from 'react-bootstrap-icons';
import Sidebar from './SideBar'; // Import Sidebar
import '../styles/NotificationAdmin.css'; // Import the CSS for the styling
import { useLocation } from 'react-router-dom';

const NotificationAdmin = () => {
  const [notifications, setNotifications] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showSidebar, setShowSidebar] = useState(true); // Sidebar state
  const location = useLocation(); // Get the current route

  const notificationsPerPage = 5; // Set how many notifications to load per page

  // Fetch notifications function
  const fetchNotifications = useCallback(async (startAfterDoc = null) => {
    setLoading(true);
    try {
      let queryRef = query(
        collection(db, 'admin', 'notifications', 'list'),
        orderBy('datetime', 'desc'),
        limit(notificationsPerPage)
      );

      if (startAfterDoc) {
        queryRef = query(queryRef, startAfter(startAfterDoc));
      }

      const notificationsSnapshot = await getDocs(queryRef);

      if (notificationsSnapshot.empty) {
        setHasMore(false);
        setLoading(false);
        return;
      }

      const notificationsList = notificationsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || 'No Title',
          description: data.description || 'No Description',
          dateTime: data.datetime ? data.datetime.toDate().toLocaleString() : 'No Date',
          unread: data.unread,
        };
      });

      // Prevent duplicate notifications
      setNotifications(prevNotifications => {
        const newNotifications = notificationsList.filter(notification => 
          !prevNotifications.some(prevNotification => prevNotification.id === notification.id)
        );
        return [...prevNotifications, ...newNotifications];
      });

      setLastVisible(notificationsSnapshot.docs[notificationsSnapshot.docs.length - 1]);
      setLoading(false);

      // Call markNotificationsAsRead for the notifications
      await markNotificationsAsRead(notificationsList);

    } catch (error) {
      console.error('Error fetching notifications:', error);
      setLoading(false);
    }
  }, []);

  const markNotificationsAsRead = async (notificationsList) => {
    try {
      for (const notification of notificationsList) {
        const notificationRef = doc(db, 'admin', 'notifications', 'list', notification.id);

        // Get the current notification document
        const docSnap = await getDoc(notificationRef);

        if (docSnap.exists()) {
          const notificationData = docSnap.data();

          // Only update if 'unread' is true
          if (notificationData.unread === true) {
            await updateDoc(notificationRef, { unread: false });
            console.log(`Notification ${notification.id} marked as read`);
          } else {
            console.log(`Notification ${notification.id} is already marked as read`);
          }
        } else {
          console.log('Notification does not exist');
        }
      }
    } catch (error) {
      console.error('Error updating notifications:', error);
      alert('An error occurred while updating the notifications.');
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const loadMoreNotifications = () => {
    if (!loading && hasMore) {
      fetchNotifications(lastVisible);
    }
  };

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar); // Toggle the sidebar
  };

  return (
    <Container
    fluid
    className="admin-dashboard d-flex"
    style={{ paddingBottom: 100 }}
  >
    <Sidebar showSidebar={showSidebar ? "show" : ""} />
    <Container
      className={`p-2 flex-grow-1 main-content ${
        showSidebar ? "shifted" : ""
      }`}
    >
      <Button variant="dark" onClick={toggleSidebar} className="mb-3">
        <List size={20} />
      </Button>

      <h3 style={{color:'#000000'}}>Notifications</h3>
      <ul className="notification-list">
        {notifications.length === 0 ? (
          <li className="no-notifications">No notifications available</li>
        ) : (
          notifications.map((notification) => (
            <li key={notification.id} className="notification-item">
              <div className="notification-content">
                <strong className="notification-title">{notification.title}</strong>
                <p className="notification-description">{notification.description}</p>
              </div>
              <small className="notification-datetime">{notification.dateTime}</small>
            </li>
          ))
        )}
      </ul>

      {loading && <p className="loading-text">Loading...</p>}

      {hasMore && !loading && (
        <p className="notification-description" style={{textDecoration:"underline",paddingLeft:5,color:'#2050ad'}} onClick={loadMoreNotifications}>Load More</p>
      )}

      {!hasMore && <p className="no-more-notifications">No more notifications available</p>}
      </Container>
      </Container>
  );
};

export default NotificationAdmin;
